import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import PageHeader from '../../components/UI/PageHeader';
import Card from '../../components/UI/Card';
import Button from '../../components/UI/Button';
import { getUserById } from '../../services/users/usersService';
import { UserDto, UserStatus } from '../../services/users/types';

const UserDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const auth = useAuth();
  const [user, setUser] = useState<UserDto | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  useEffect(() => {
    if (id) {
      fetchUserDetails(id);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);
  
  const fetchUserDetails = async (userId: string) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const userData = await getUserById(auth.user || null, userId);
      setUser(userData);
    } catch (err) {
      setError('Failed to fetch user details. Please try again.');
      console.error('Error fetching user details:', err);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Function to get the appropriate badge color based on status
  const getStatusBadgeClasses = (status?: UserStatus) => {
    switch (status) {
      case UserStatus.ACTIVATED:
        return 'bg-green-100 text-green-800';
      case UserStatus.INACTIVATED:
        return 'bg-gray-100 text-gray-800';
      case UserStatus.BLOCKED:
        return 'bg-red-100 text-red-800';
      case UserStatus.RECONFIGURE:
        return 'bg-yellow-100 text-yellow-800';
      case UserStatus.ONBOARDING_IN_PROGRESS:
        return 'bg-blue-100 text-blue-800';
      case UserStatus.TICKET_OPENING:
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  
  return (
    <div>
      <PageHeader
        title={isLoading ? 'Loading...' : error ? 'Error' : !user ? 'Not Found' : user.first_name && user.last_name ? `${user.first_name} ${user.last_name}` : user.username || 'User Details'}
        breadcrumbs={[
          { label: "Dashboard", to: "/" },
          { label: "Users", to: "/users" },
          { label: isLoading ? 'Loading...' : error ? 'Error' : !user ? 'Not Found' : user.first_name && user.last_name ? `${user.first_name} ${user.last_name}` : user.username || 'Details' }
        ]}
      />
      
      <Card>
        {isLoading ? (
          <div className="flex justify-center items-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          </div>
        ) : error ? (
          <div className="bg-red-50 text-red-700 p-4 rounded-md mb-4">
            {error}
            <Button 
              variant="ghost" 
              size="sm" 
              className="ml-2"
              onClick={() => id && fetchUserDetails(id)}
            >
              Retry
            </Button>
          </div>
        ) : !user ? (
          <div className="bg-yellow-50 text-yellow-700 p-4 rounded-md mb-4">
            User not found
            <Link to="/users">
              <Button 
                variant="ghost" 
                size="sm" 
                className="ml-2"
              >
                Back to Users
              </Button>
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 p-6">
            <div className="md:col-span-2">
              <h3 className="text-lg font-medium text-gray-900 mb-4">User Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <span className="text-sm font-medium text-gray-500 block">Username</span>
                  <span className="mt-1 block">{user.username || '-'}</span>
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-500 block">Email</span>
                  <span className="mt-1 block">{user.email || '-'}</span>
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-500 block">Name</span>
                  <span className="mt-1 block">
                    {user.first_name && user.last_name 
                      ? `${user.first_name} ${user.last_name}`
                      : user.first_name || user.last_name || '-'}
                  </span>
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-500 block">Phone</span>
                  <span className="mt-1 block">{user.phone_number || '-'}</span>
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-500 block">Status</span>
                  <span className="mt-1 block">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeClasses(user.status)}`}>
                      {user.status || 'Unknown'}
                    </span>
                  </span>
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-500 block">Application ID</span>
                  <span className="mt-1 block">{user.application_id || '-'}</span>
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-500 block">Created At</span>
                  <span className="mt-1 block">
                    {user.created_at ? new Date(user.created_at).toLocaleString() : '-'}
                  </span>
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-500 block">Updated At</span>
                  <span className="mt-1 block">
                    {user.updated_at ? new Date(user.updated_at).toLocaleString() : '-'}
                  </span>
                </div>
              </div>
            </div>
            
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Actions</h3>
              <div className="space-y-3">
                <Link to={`/users/${id}/chat`} className="block w-full">
                  <Button variant="secondary" fullWidth>
                    <i className="fas fa-comment-dots mr-2"></i>
                    Chat History
                  </Button>
                </Link>
                <Link to={`/users/${id}/payments`} className="block w-full">
                  <Button variant="secondary" fullWidth>
                    <i className="fas fa-credit-card mr-2"></i>
                    Payments
                  </Button>
                </Link>
                <Link to={`/users/${id}/schedules`} className="block w-full">
                  <Button variant="secondary" fullWidth>
                    <i className="fas fa-calendar-alt mr-2"></i>
                    Schedules
                  </Button>
                </Link>
                <Link to={`/users/${id}/devices`} className="block w-full">
                  <Button variant="secondary" fullWidth>
                    <i className="fas fa-mobile-alt mr-2"></i>
                    Devices
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
};

export default UserDetails;